import type { ReactNode } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { MetricInfo } from './metric-info';

interface MetricCardFrameProps {
  title: string;
  /** Shown on hover of an info icon; no icon without it. */
  help?: string;
  details?: string[];
  className?: string;
  action?: ReactNode;
  children: ReactNode;
}

/** The Sentinel Analytics card: its title (with any help on hover), then the content. */
export function MetricCardFrame({ title, help, details, className, action, children }: MetricCardFrameProps) {
  return (
    <section aria-label={title} className={cn('relative flex min-h-28 min-w-0 flex-col rounded-3xl border border-gray-500 bg-white px-[15px] py-[11px]', className)}>
      <div className='flex items-center justify-between gap-2'>
        <div className='flex items-center gap-1.5 text-[13px] font-medium leading-[18px] text-ink'>
          <h3>{title}</h3>
          {help ? <MetricInfo label={help} details={details} /> : null}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

interface MetricCardProps {
  title: string;
  description: string;
  /** The headline number. For count cards: the count. For percent cards: the percent. */
  value: number | string | null;
  /** Optional denominator shown as "/N" after the value (e.g. "360 /1,000"). */
  denominator?: number | string | null;
  /** Suffix appended to value when present (e.g. "%"). */
  unit?: string;
  /** The trend number to display; positive = up, negative = down. */
  trend?: number | null;
  /** Human-readable comparison period shown inside the trend tooltip. */
  comparisonLabel?: string;
  isLoading?: boolean;
}

const formatTrend = (n: number) => {
  const abs = Math.abs(n);
  return Number.isInteger(abs) ? `${abs}%` : `${abs.toFixed(1)}%`;
};

export function MetricCard({
  title,
  description,
  value,
  denominator,
  unit,
  trend,
  comparisonLabel,
  isLoading = false,
}: MetricCardProps) {
  const showTrend = !isLoading && trend !== null && trend !== undefined;
  const isUp = (trend ?? 0) >= 0;

  return (
    <MetricCardFrame title={title}>
      <div className='mt-auto flex min-h-8 items-center gap-3 pt-6'>
        {isLoading ? (
          <>
            <Skeleton className='h-7 w-24 rounded-md' />
            <Skeleton className='h-5 w-14 rounded-full' />
          </>
        ) : (
          <>
            <div className='flex items-center gap-1'>
              <span className='text-[20px] leading-[28px] font-semibold text-[#2b2e34]'>
                {value === null || value === undefined ? '—' : `${value}${unit ?? ''}`}
              </span>
              {denominator !== null && denominator !== undefined ? (
                <span className='text-[13px] leading-[18px] font-normal text-[#5f656f]'>
                  /{typeof denominator === 'number' ? denominator.toLocaleString() : denominator}
                </span>
              ) : null}
            </div>
            {showTrend ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-2xl text-[12px] leading-[16px] font-normal cursor-help',
                        isUp ? 'bg-[#dcfce7] text-[#22c55e]' : 'bg-[#fee2e2] text-[#ef4444]',
                      )}
                    >
                      {isUp ? <ArrowUp className='h-3 w-3' /> : <ArrowDown className='h-3 w-3' />}
                      {formatTrend(trend ?? 0)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className='text-xs'>
                      {comparisonLabel ? `vs ${comparisonLabel}` : 'vs previous period'}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : null}
          </>
        )}
      </div>
      <p className='text-[13px] leading-[18px] text-gray-900'>{description}</p>
    </MetricCardFrame>
  );
}
